import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  headingEffect,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-8 md:gap-10">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          headingEffect={headingEffect}
          reveal
        />
        <div
          className={cn("grid gap-x-6 gap-y-12", columns[columnCount || 3])}
          data-reveal="stagger"
        >
          {items.map((member, index) => (
            <article className="group" key={index}>
              <DaylightMedia
                aspectRatio="3:4"
                className="bg-sky"
                imageClassName="transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                media={member.portrait.image}
                effect={member.portrait.effect}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="pt-5">
                <h3 className="font-daylight-display text-2xl font-semibold leading-tight">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-2 inline-flex rounded-full bg-sky px-3 py-1 font-daylight-body text-sm font-semibold text-cobalt">
                    {member.role}
                  </p>
                ) : null}
                {member.biography ? (
                  <DaylightRichText
                    className="mt-4 text-base text-basalt md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-5 flex flex-wrap gap-4 border-t border-weathered-steel pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-daylight-mono text-xs text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
